import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    num: '01',
    icon: 'ri-building-2-line',
    title: 'Hotel Management',
    description:
      'Best-in-class hotel operations tailored to each asset\'s unique needs. We deliver measurable improvements in guest satisfaction, operational efficiency, and GOP margins across branded, independent, select service, full service, and extended stay properties.',
  },
  {
    num: '02',
    icon: 'ri-megaphone-line',
    title: 'Sales & Marketing',
    description:
      'Strategy-driven sales and data-backed decisions. We deploy hyper-local outreach, regional penetration, and national account optimization — executed by a seasoned sales force that builds lasting client relationships.',
  },
  {
    num: '03',
    icon: 'ri-line-chart-line',
    title: 'Revenue Management',
    description:
      'We drive ADR and premium revenue through tightly integrated revenue, sales, and digital marketing strategies. Leveraging advanced forecasting models and channel-agnostic revenue plans — we prioritize profitability across GDS, OTA, direct, and metasearch.',
  },
  {
    num: '04',
    icon: 'ri-hammer-line',
    title: 'Development',
    description:
      'From blueprint to grand opening. Our pre-opening expertise covers underwriting, brand selection, hiring, training, systems integration, and go-to-market positioning — always with long-term asset value in mind.',
  },
  {
    num: '05',
    icon: 'ri-tools-line',
    title: 'Renovation',
    description:
      'We lead CapEx planning, brand negotiation, PIP evaluations, and full renovation oversight — ensuring capital is deployed efficiently to drive ROI and enhance guest satisfaction.',
  },
  {
    num: '06',
    icon: 'ri-shield-check-line',
    title: 'Quality Assurance',
    description:
      'Rigorous quality control through daily reporting, KPI tracking, inline audits, and competitive benchmarking. We protect your brand reputation and guest loyalty scores through continuous improvement.',
  },
];

export default function ServicesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Services</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h2 className="text-5xl lg:text-6xl font-light text-white leading-tight">
                Ten Ways We
                <br />
                <span className="text-amber-400 italic">Move the Needle</span>
              </h2>
            </div>
            <div className="flex flex-col items-start gap-4">
              <p className="text-base text-gray-400 font-light max-w-sm leading-relaxed">
                From daily operations to 10-year asset strategy — we cover the full picture.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center space-x-2 text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors whitespace-nowrap group"
              >
                <span>View All Services</span>
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
              </Link>
            </div>
          </div>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-800/50">
          {services.map((service, index) => (
            <div
              key={index}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              className={`relative bg-slate-950 p-8 cursor-default transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${hovered === index ? 'bg-slate-900' : ''}`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              {/* Number */}
              <span className="text-xs font-medium text-amber-500/60 tracking-[0.3em] mb-6 block">{service.num}</span>

              {/* Icon */}
              <div className={`w-10 h-10 flex items-center justify-center mb-5 transition-all duration-300 ${hovered === index ? 'text-amber-400' : 'text-gray-500'}`}>
                <i className={`${service.icon} text-3xl`}></i>
              </div>

              {/* Title */}
              <h3 className={`text-xl font-medium mb-3 transition-colors duration-300 ${hovered === index ? 'text-amber-400' : 'text-white'}`}>
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-400 font-light leading-relaxed mb-5">
                {service.description}
              </p>

              {/* Learn More */}
              <Link
                to="/services"
                className={`inline-flex items-center space-x-1.5 text-xs font-medium uppercase tracking-widest transition-colors duration-300 ${
                  hovered === index ? 'text-amber-400' : 'text-gray-600'
                }`}
              >
                <span>Learn More</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>

              {/* Bottom border accent */}
              <div className={`absolute bottom-0 left-0 h-0.5 bg-amber-500 transition-all duration-500 ${hovered === index ? 'w-full' : 'w-0'}`}></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-12 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <Link
            to="/services"
            className="inline-flex items-center space-x-2 px-8 py-4 rounded-full border border-amber-500/40 text-amber-400 text-sm font-medium tracking-wide hover:bg-amber-500/10 transition-all duration-300 whitespace-nowrap group"
          >
            <span>See All Services — Asset Management, HR, Finance, IT &amp; More</span>
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
